import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import workflowService from '../../../services/workflowService';

const WorkflowAnalytics = ({ workflows, userId }) => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      loadAnalytics();
    }
  }, [userId, workflows]);

  const loadAnalytics = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await workflowService?.getWorkflowAnalytics(userId);
      
      if (fetchError) {
        setError(fetchError);
        return;
      }
      
      setAnalyticsData(data || []);
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallMetrics = () => {
    if (!analyticsData?.length) {
      return {
        totalWorkflows: 0,
        activeWorkflows: 0,
        totalExecutions: 0,
        totalSuccesses: 0,
        overallSuccessRate: 0
      };
    }

    const totalExecutions = analyticsData?.reduce((sum, w) => sum + (w?.execution_count || 0), 0);
    const totalSuccesses = analyticsData?.reduce((sum, w) => sum + (w?.success_count || 0), 0);
    
    return {
      totalWorkflows: analyticsData?.length,
      activeWorkflows: analyticsData?.filter(w => w?.status === 'active')?.length,
      totalExecutions,
      totalSuccesses,
      overallSuccessRate: totalExecutions > 0 ? Math.round((totalSuccesses / totalExecutions) * 100) : 0
    };
  };

  const getTopPerformingWorkflows = () => {
    return analyticsData
      ?.filter(w => w?.execution_count > 0)
      ?.sort((a, b) => {
        const aRate = (a?.success_count / a?.execution_count) * 100;
        const bRate = (b?.success_count / b?.execution_count) * 100;
        return bRate - aRate;
      })
      ?.slice(0, 5);
  };

  const getWorkflowsByStatus = () => {
    const statusCounts = analyticsData?.reduce((acc, workflow) => {
      acc[workflow?.status] = (acc?.[workflow?.status] || 0) + 1;
      return acc;
    }, {});

    return [
      { status: 'active', count: statusCounts?.active || 0, color: 'text-success', icon: 'Play' },
      { status: 'draft', count: statusCounts?.draft || 0, color: 'text-muted-foreground', icon: 'FileText' },
      { status: 'paused', count: statusCounts?.paused || 0, color: 'text-warning', icon: 'Pause' },
      { status: 'completed', count: statusCounts?.completed || 0, color: 'text-primary', icon: 'CheckCircle' },
      { status: 'archived', count: statusCounts?.archived || 0, color: 'text-muted-foreground', icon: 'Archive' }
    ];
  };

  const metrics = calculateOverallMetrics();
  const topWorkflows = getTopPerformingWorkflows();
  const statusDistribution = getWorkflowsByStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={20} className="text-destructive" />
          <span className="text-destructive">{error}</span>
        </div>
      </div>
    );
  }

  if (analyticsData?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="BarChart3" size={64} className="mx-auto mb-6 text-muted-foreground opacity-50" />
        <h3 className="text-xl font-semibold text-foreground mb-2">
          No Analytics Available
        </h3>
        <p className="text-muted-foreground">
          Create and execute workflows to see performance analytics
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Workflows</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.totalWorkflows}</p>
            </div>
            <Icon name="Zap" size={24} className="text-primary" />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Workflows</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.activeWorkflows}</p>
            </div>
            <Icon name="Play" size={24} className="text-success" />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Executions</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.totalExecutions}</p>
            </div>
            <Icon name="Target" size={24} className="text-primary" />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Successful Executions</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.totalSuccesses}</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.overallSuccessRate}%</p>
            </div>
            <Icon name="TrendingUp" size={24} className="text-warning" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Workflow Status Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Workflow Status Distribution
          </h3>
          <div className="space-y-3">
            {statusDistribution?.map(status => (
              <div key={status?.status} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name={status?.icon} size={16} className={status?.color} />
                  <span className="text-sm font-medium text-foreground capitalize">
                    {status?.status}
                  </span>
                </div>
                <span className="text-sm font-bold text-foreground">
                  {status?.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Workflows */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Top Performing Workflows
          </h3>
          {topWorkflows?.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No executed workflows yet
            </p>
          ) : (
            <div className="space-y-3">
              {topWorkflows?.map((workflow, index) => {
                const successRate = Math.round((workflow?.success_count / workflow?.execution_count) * 100);
                return (
                  <div key={workflow?.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {workflow?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {workflow?.execution_count} executions
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-success">
                        {successRate}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        success rate
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detailed Workflow Analytics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Detailed Workflow Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                  Workflow Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                  Executions
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                  Success
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                  Failures
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                  Success Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {analyticsData?.map(workflow => {
                const successRate = workflow?.execution_count > 0 
                  ? Math.round((workflow?.success_count / workflow?.execution_count) * 100)
                  : 0;
                return (
                  <tr key={workflow?.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-foreground">{workflow?.name}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center space-x-1 text-xs font-medium capitalize ${
                        workflow?.status === 'active' ? 'text-success' :
                        workflow?.status === 'paused' ? 'text-warning' :
                        workflow?.status === 'completed'? 'text-primary' : 'text-muted-foreground'
                      }`}>
                        {workflow?.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-foreground">
                      {workflow?.execution_count || 0}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-success">
                      {workflow?.success_count || 0}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-destructive">
                      {workflow?.failure_count || 0}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-foreground">
                      {successRate}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkflowAnalytics;